import { TranslocoPipe, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from 'core';
import { BankBranch } from 'receipts';
import { debounceTime, filter, switchMap, tap } from 'rxjs';
import { OrderBusinessType, OrderPaymentOption } from '../../interfaces/order-proposal.model';
import { OrderProposalService } from '../../services/order-proposal.service';

type BankBranchOption = BankBranch & { descrizione: string | null };

@Component({
  providers: [provideTranslocoScope('orders')],
  selector: 'ord-select-payment',
  imports: [TranslocoPipe, MatAutocompleteModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatRadioModule, ReactiveFormsModule],
  templateUrl: './select-payment.html',
  styleUrl: './select-payment.scss',
})
export class SelectPayment implements OnInit, OnDestroy {
  private readonly headerService = inject(HeaderService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly proposalService = inject(OrderProposalService);

  private readonly proposalId = this.route.snapshot.queryParamMap.get('id') ?? '';
  readonly type = (this.route.snapshot.queryParamMap.get('type') ?? 'Oil') as OrderBusinessType;

  // Condizioni proponibili per la tipologia, l'abituale compresa
  readonly options = signal<OrderPaymentOption[]>([]);
  readonly idDefault = signal<string | null>(null);
  readonly selectedId = signal<string | null>(null);
  readonly selectedOption = computed(() => this.options().find(option => option.value === this.selectedId()) ?? null);

  // Secondo passo: i dati che la condizione chiede (banca o contatto per la consegna)
  readonly step = signal<'select' | 'details'>('select');
  readonly saving = signal(false);

  readonly abiControl = new FormControl('', { nonNullable: true });
  readonly cabControl = new FormControl('', { nonNullable: true });
  readonly bancaControl = new FormControl('', { nonNullable: true });
  readonly phoneControl = new FormControl('', { nonNullable: true });
  // ABI: banche; CAB: agenzie della banca scelta o per CAB; banca: agenzie per nome
  readonly abiOptions = signal<BankBranch[]>([]);
  readonly cabOptions = signal<BankBranchOption[]>([]);
  readonly bancaOptions = signal<BankBranchOption[]>([]);
  readonly bank = signal<BankBranch | null>(null);

  ngOnInit(): void {
    this.headerService.setTitle('orders.ui.nuovaPropostaDOrdine');

    this.proposalService.ensure(this.proposalId).pipe(
      switchMap(() => this.proposalService.getSectionPayments(this.type)),
    ).subscribe(options => {
      const section = this.proposalService.section(this.type);
      this.options.set(options);
      this.idDefault.set(section?.idCodicePagamentoAbituale ?? null);
      // Senza una condizione in vigore si parte dalla prima, l'abituale
      this.selectedId.set(section?.idCodicePagamento ?? options[0]?.value ?? null);

      // I dati già salvati valgono per la stessa condizione
      if (section?.abi) {
        this.selectBank({ abi: section.abi, cab: section.cab ?? '', banca: section.banca ?? '' });
      }
      if (section?.datoAggiuntivoPagamento === 'CONTATTO') {
        this.phoneControl.setValue(section.destino?.nota?.contatto ?? '');
      }
    });

    // La scelta arriva dall'autocomplete come oggetto: si cerca solo sul testo scritto
    this.abiControl.valueChanges
      .pipe(
        filter(value => typeof value === 'string'),
        tap(() => {
          this.bank.set(null);
          this.cabOptions.set([]);
        }),
        debounceTime(300),
        switchMap(text => this.proposalService.searchBanks(text)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(banks => this.abiOptions.set(banks));

    // Con la banca scelta si cercano i suoi CAB, senza solo sul CAB scritto
    this.cabControl.valueChanges
      .pipe(
        filter(value => typeof value === 'string'),
        tap(() => {
          const bank = this.bank();
          this.bank.set(bank?.abi ? { ...bank, cab: '' } : null);
        }),
        debounceTime(300),
        switchMap(text => this.proposalService.searchCabs(this.bank()?.abi ?? null, text)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(branches => this.cabOptions.set(branches));

    this.bancaControl.valueChanges
      .pipe(
        filter(value => typeof value === 'string'),
        tap(() => this.bank.set(null)),
        debounceTime(300),
        switchMap(text => this.proposalService.searchBankBranches(text)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(branches => this.bancaOptions.set(branches));

    // Solo cifre nel contatto telefonico
    this.phoneControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        const digits = value.replace(/\D/g, '');
        if (digits !== value) {
          this.phoneControl.setValue(digits, { emitEvent: false });
        }
      });
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
  }

  optionLabel(option: OrderPaymentOption): string {
    return option.value === this.idDefault() ? `${option.label} (default)` : option.label;
  }

  onSelectOption(id: string): void {
    this.selectedId.set(id);
  }

  // Agenzia scelta (dal CAB o dal nome): ABI, CAB e banca insieme
  selectBank(branch: BankBranch): void {
    this.bank.set(branch);
    this.abiControl.setValue(branch.abi, { emitEvent: false });
    this.cabControl.setValue(branch.cab, { emitEvent: false });
    this.bancaControl.setValue(branch.banca, { emitEvent: false });
    this.loadCabs(branch.abi);
  }

  // Banca scelta dall'ABI: il CAB si sceglie fra le sue agenzie, già pronte all'apertura
  selectAbi(bank: BankBranch): void {
    this.selectBank({ abi: bank.abi, cab: '', banca: bank.banca });
  }

  private loadCabs(abi: string): void {
    this.proposalService.searchCabs(abi, '').subscribe(branches => this.cabOptions.set(branches));
  }

  get bankComplete(): boolean {
    const bank = this.bank();
    return !!bank?.abi && !!bank.cab && !!bank.banca;
  }

  // La condizione senza dati in più si salva subito, le altre chiedono banca o contatto
  continue(): void {
    const option = this.selectedOption();
    if (!option) {
      return;
    }

    if (option.type === 'ABICAB' || option.type === 'CONTATTO') {
      this.step.set('details');
      return;
    }

    this.save({ idCodicePagamento: option.value, abi: null, cab: null, contatto: null });
  }

  confirmDetails(): void {
    const option = this.selectedOption()!;
    if (option.type === 'ABICAB') {
      this.save({ idCodicePagamento: option.value, abi: this.bank()!.abi, cab: this.bank()!.cab, contatto: null });
    } else {
      this.save({ idCodicePagamento: option.value, abi: null, cab: null, contatto: this.phoneControl.value });
    }
  }

  // Anche dal secondo passo si esce senza salvare nulla
  cancel(): void {
    this.navigateBack();
  }

  private save(changes: { idCodicePagamento: string; abi: string | null; cab: string | null; contatto: string | null }): void {
    this.saving.set(true);
    this.proposalService.setSection(this.type, changes).subscribe({
      next: () => {
        this.snackBar.open(translate<string>('orders.ui.metodoDiPagamentoAggiornato'), translate<string>('orders.ui.chiudi'), { duration: 5000 });
        this.navigateBack();
      },
      error: () => this.saving.set(false),
    });
  }

  private navigateBack(): void {
    void this.router.navigate(['/new-order-proposal/shipping-payment'], {
      queryParams: { id: this.proposalId },
      replaceUrl: true,
    });
  }
}
